import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Col, Form, Result, Row, Skeleton } from 'antd';

import { Content } from './styles';
import { Actions } from './Actions';
import { Priority } from './Priority';
import { InfoTooltip } from './InfoTooltip';
import { useRosaContext } from '../../context';
import { OtherElements } from './OtherElements';
import type { FormRequiredElement } from './types';
import { NextButton, PreviousButton } from '../../styles';
import { Paragraph, Title } from '@/components/Typography';
import { useGetEquipments } from '../../hooks/useGetEquipments';
import { useCreateRequiredEquipment } from '../../hooks/useCreateRequiredEquipment';

const { useFormInstance } = Form;

export function RequiredElements() {
	const form = useFormInstance();
	const { organization_id, company_id, report_id, onNextStep, onPreviousStep } = useRosaContext();

	const { mutateAsync: createEquipment } = useCreateRequiredEquipment();
	const { data, isLoading, isError } = useGetEquipments({ organization_id, company_id });

	const stepName = 'required_elements';

	async function handleSubmit(): Promise<void> {
		const formData = await form.validateFields();
		const requiredElements = formData[stepName];

		const equipments = formatElements(requiredElements);

		const payload = {
			equipments,
			company_id,
			organization_id,
			report_id
		};

		await createEquipment(payload);

		onNextStep();
	}

	function formatElements(requiredElements: FormRequiredElement): any {
		let filledFields = [] as any;

		data?.forEach((element) => {
			const formField = requiredElements[element.name];
			if (formField && formField.priority && formField.action) {
				filledFields.push({ ...formField, id: element.id });
			}
		});

		return filledFields;
	}

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError) {
		return (
			<Result
				status="error"
				title={I18n.get('Submission Failed')}
				subTitle={I18n.get('Please check and modify the following information before resubmitting.')}
			/>
		);
	}

	return (
		<Row gutter={[0, 30]}>
			<Col span={24}>
				<Row align="middle">
					<Col xs={12}>
						<Title level={4}>{I18n.get('Required elements')}</Title>
					</Col>
					<Col xs={24}>
						<Paragraph>{I18n.get('Prioritize the required elements')}</Paragraph>
					</Col>
				</Row>
			</Col>
			<Content span={24}>
				<Row gutter={[0, 60]}>
					<Col span={24}>
						<Row gutter={[0, 30]}>
							{data?.map((equipment, index) => {
								const isSecondItem = index === 1;
								const span = isSecondItem ? 16 : 8;

								const fieldName = [stepName, equipment.name];

								return (
									<Col span={span} key={equipment.id}>
										<Row gutter={[5, 15]}>
											<Col span={24}>
												<Title level={5}>{equipment.description}</Title>
											</Col>
											<Col span={24}>
												<Actions fieldName={fieldName} />
											</Col>
											<Col lg={isSecondItem ? 6 : 12} xxl={isSecondItem ? 5 : 10}>
												<Priority fieldName={fieldName} />
											</Col>
											<Col>
												<InfoTooltip />
											</Col>
										</Row>
									</Col>
								);
							})}
						</Row>
					</Col>
					<Col span={24}>
						<OtherElements />
					</Col>
				</Row>
			</Content>
			<Col span={24}>
				<Row justify="center" gutter={[30, 0]}>
					<Col>
						<PreviousButton type="ghost" onClick={onPreviousStep}>
							{I18n.get('Back')}
						</PreviousButton>
					</Col>
					<Col>
						<NextButton type="primary" onClick={handleSubmit}>
							{I18n.get('Next')}
						</NextButton>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
